import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaperclip,
    faCircleQuestion,
    faClockRotateLeft,
    faEllipsisVertical,
    faMicrophone,
    faPaperPlane,
    faRobot,
    faStop,
    faScrewdriverWrench,
    faThumbsDown,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { useApplicationContext } from 'ApplicationContext';
import { ProjectInput } from 'apiClient/Api';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';
import { useDictation } from 'Hooks/useDictation';
import { MarkdownRenderer } from 'Components/MarkdownRenderer';
import { AttachmentBar } from './AttachmentBar';
import { AiFeedbackDialog, FeedbackKind } from './AiFeedbackDialog';

interface DraftDto {
    id: string;
    name: string;
    status: string;
    projektId: string | null;
    updatedAt: string;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    iterations: number;
    durationMs: number;
    anlagenart: string | null;
    projektart: string | null;
    openQuestions: number;
    /** Ältere Züge stehen zusammengefasst im Kontext, statt jedes Mal vollständig mitzugehen. */
    compacted: boolean;
}

interface ParticipantDto {
    name: string;
    organisation: string | null;
    mail: string | null;
    role: string;
    language: string | null;
    note: string | null;
}

interface QuestionDto {
    number: number;
    text: string;
    detail: string | null;
    recipient: string | null;
    language: string | null;
    state: string;
    answer: string | null;
}

/**
 * Was aus dem Gespräch laufend in die Kalkulation wandert — **ohne** die Positionen.
 *
 * Der Unterschied zum Übernehmen ist der Punkt: an der Anforderungsauswahl arbeitet der Vertriebler
 * parallel selbst, sie ihm nach jedem Zug unter den Händen zu ersetzen wäre unbrauchbar.
 * Angebotstexte und Sonderleistungen schreibt dagegen nur der Assistent — die dürfen nachfließen,
 * damit beim Übernehmen nichts fehlt und ein abgebrochenes Telefonat trotzdem etwas hinterlässt.
 */
interface PartialDto {
    vorbemerkung: string | null;
    projektbeschreibung: string | null;
    zusammenfassung: string | null;
    freiePositionen: ProjectInput['freiePositionen'];
}

interface MessageDto {
    role: string;
    text: string;
    toolCalls: number;
}

interface ProtocolEntryDto {
    topic: string;
    question: string;
    answer: string;
    outcome: string | null;
    recordedAt: string;
}

interface DetailDto {
    draft: DraftDto;
    messages: MessageDto[];
    protocol: ProtocolEntryDto[];
    participants: ParticipantDto[];
    questions: QuestionDto[];
    partial: PartialDto;
    summary: string | null;
}

interface TurnDto {
    ok: boolean;
    reply: string | null;
    error: string | null;
    toolCalls: number;
    elapsedMilliseconds: number;
    draft: DraftDto;
    partial: PartialDto | null;
    questions: QuestionDto[] | null;
}

interface AiAssistantPanelProps {
    /** Name des Gesprächs — meist der Kundenname aus dem Kalkulator. */
    name: string;
    /**
     * Die geladene Kalkulation, falls es eine gibt. Damit findet der Assistent das Gespräch wieder,
     * das zu diesem Projekt gehört, statt ein neues anzufangen.
     */
    projektId?: string | null;
    /**
     * Übernimmt den erarbeiteten Stand. Die Gesprächsnotiz kommt getrennt mit — sie gehört in die
     * internen Notizen und nicht ins Angebot.
     */
    onApply: (project: ProjectInput, notes?: string | null) => void;
    /**
     * Übernimmt den Teilstand: Angebotstexte und Sonderleistungen, ohne die Positionen anzufassen.
     *
     * Wird nach jedem Zug gerufen. Der Vertriebler soll die Texte nicht am Gesprächsende
     * „einsammeln" müssen — bricht ein Telefonat ab, ist trotzdem etwas in der Kalkulation.
     */
    onPartial?: (partial: PartialDto) => void;
    /** Schließt den Bereich. Das Gespräch bleibt erhalten. */
    onClose?: () => void;
}

/**
 * Der Assistent als Seitenbereich während des Telefonats.
 *
 * **Kein Chatfenster im üblichen Sinn.** Der Assistent spricht nicht mit dem Kunden, sondern
 * souffliert dem Vertriebler: oben der Verlauf, unten die Eingabe, dazwischen die laufenden
 * Werkzeugschritte. Die Werkzeugrunden erscheinen als eine Zeile je Antwort und nicht als eigene
 * Blasen — sonst ersäuft der Dialog im Protokoll.
 */
export const AiAssistantPanel: React.FC<AiAssistantPanelProps> = ({
    name, projektId, onApply, onPartial, onClose,
}) => {
    const context = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [draft, setDraft] = useState<DraftDto | null>(null);

    /**
     * Unterlagen: Zähler statt Liste.
     *
     * Die Leiste lädt ihre Liste selbst; hier wird nur angestoßen, dass sie das nach einem
     * Hochladen erneut tut. Zwei Stellen, die dieselbe Liste halten, laufen sonst auseinander.
     */
    const [attachmentToken, setAttachmentToken] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const [messages, setMessages] = useState<MessageDto[]>([]);
    const [protocol, setProtocol] = useState<ProtocolEntryDto[]>([]);
    const [questions, setQuestions] = useState<QuestionDto[]>([]);
    const [participants, setParticipants] = useState<ParticipantDto[]>([]);
    const [showCaseFile, setShowCaseFile] = useState(false);

    /**
     * Frühere Gespräche für die Wiederaufnahme.
     *
     * Der Verlauf lag längst in der Datenbank, nur kam man nicht mehr an ihn heran: die Oberfläche
     * legte bei jedem Öffnen ein neues Gespräch an. Ruft der Kunde am nächsten Tag zurück, wurde
     * damit alles noch einmal erzählt.
     */
    const [recent, setRecent] = useState<DraftDto[]>([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [starting, setStarting] = useState(false);
    const [currentTool, setCurrentTool] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [feedbackKind, setFeedbackKind] = useState<FeedbackKind | null>(null);
    const [appliedAt, setAppliedAt] = useState<Date | null>(null);

    // Diktiertes hängt an die Eingabe an — nachschieben ist im Gespräch der Normalfall.
    const dictation = useDictation(context.api, (text) =>
        setInput((prev) => (prev.trim().length > 0 ? `${prev.trim()} ${text}` : text)));

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const hubRef = useRef<HubConnection | null>(null);

    /** Nach jedem Zug ans Ende springen — der Vertriebler will die neueste Frage sehen. */
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, currentTool]);

    /**
     * Live-Fortschritt. Ohne das sieht der Vertriebler bis zu einer Minute lang nur einen Kreisel
     * und weiß nicht, ob noch etwas passiert.
     */
    useEffect(() => {
        if (!draft) return undefined;

        const connection = new HubConnectionBuilder()
            .withUrl(`${context.baseUrl}/hubs/ai-draft`, { accessTokenFactory: () => context.token ?? '' })
            .withAutomaticReconnect()
            .build();

        connection.on('toolStarted', (payload: { tool: string }) => setCurrentTool(payload.tool));

        connection.start()
            .then(() => connection.invoke('Subscribe', draft.id))
            .catch(() => {
                // Fortschritt ist Komfort — der Dialog funktioniert auch ohne, nur stiller.
            });

        hubRef.current = connection;
        return () => {
            void connection.stop();
            hubRef.current = null;
        };
    }, [draft, context.baseUrl, context.token]);

    /**
     * Übergibt die Unterlagen an die Kalkulation, sobald es eine gibt.
     *
     * Der Moment, in dem aus Arbeitsmaterial ein Beleg wird: eine Datei am Gespräch verschwindet mit
     * dem Gesprächskontext, eine Datei an einer Kalkulation ist Kundendokumentation und bleibt. Der
     * Auslöser ist das Speichern des Projekts — genau dann bekommt dieses Panel eine `projektId`.
     */
    useEffect(() => {
        if (!draft || !projektId) return;

        http.post(`/AiDraft/${draft.id}/handover/${projektId}`).catch(() => {
            // Kein Grund für eine Meldung im Telefonat: die Dateien liegen weiterhin am Gespräch,
            // und der nächste Versuch kommt beim nächsten Speichern.
        });
    }, [draft, projektId, http]);

    const start = useCallback(async () => {
        setStarting(true);
        setError(null);
        try {
            const r = await http.post<DraftDto>('/AiDraft', undefined, { name, projektId: projektId ?? null });
            setDraft(r.data);
            setMessages([]);
            setProtocol([]);
            setQuestions([]);
            setParticipants([]);
        } catch (e) {
            context.apiError(e);
        } finally {
            setStarting(false);
        }
    }, [http, name, projektId, context]);

    const reload = useCallback(async (id: string) => {
        const r = await http.get<DetailDto>(`/AiDraft/${id}`);
        setDraft(r.data.draft);
        setMessages(r.data.messages);
        setProtocol(r.data.protocol);
        setQuestions(r.data.questions ?? []);
        setParticipants(r.data.participants ?? []);

        // Auch beim Wiederaufnehmen: was der Assistent früher geschrieben hat, gehört in die
        // Kalkulation. Sonst steht nach dem Fortsetzen ein leeres Angebot neben einem vollen Chat.
        if (r.data.partial) onPartial?.(r.data.partial);
    }, [http, onPartial]);

    /**
     * Setzt ein früheres Gespräch fort.
     *
     * Der ganze Zweck des gespeicherten Verlaufs. Der Server verdichtet ältere Züge von allein, also
     * kostet ein fortgesetztes Gespräch nicht mehr als ein frisches — nur weiß es mehr.
     */
    const resume = useCallback(async (id: string) => {
        setStarting(true);
        setError(null);
        try {
            await reload(id);
        } catch (e) {
            context.apiError(e);
        } finally {
            setStarting(false);
        }
    }, [reload, context]);

    /**
     * Lädt die früheren Gespräche, solange keines offen ist.
     *
     * Nicht automatisch öffnen: welches Gespräch gemeint ist, weiß nur der Vertriebler — ein
     * falsches automatisch fortzusetzen wäre schlimmer als ein Klick.
     */
    useEffect(() => {
        if (draft) return;

        let abgebrochen = false;
        http.get<DraftDto[]>('/AiDraft')
            .then((r) => {
                if (abgebrochen) return;
                // Das Gespräch zur geladenen Kalkulation zuerst — danach nach Datum.
                const liste = [...r.data].sort((a, b) => {
                    const passt = (d: DraftDto) => (projektId && d.projektId === projektId ? 0 : 1);
                    return passt(a) - passt(b) || b.updatedAt.localeCompare(a.updatedAt);
                });
                setRecent(liste.slice(0, 6));
            })
            .catch(() => {
                // Ohne die Liste beginnt man eben ein neues Gespräch — kein Grund für eine Meldung.
            });

        return () => { abgebrochen = true; };
    }, [draft, http, projektId]);

    const send = async () => {
        if (!draft || !input.trim()) return;

        const text = input.trim();
        setInput('');
        setBusy(true);
        setError(null);
        // Den eigenen Beitrag sofort zeigen, nicht erst nach der Antwort.
        setMessages((m) => [...m, { role: 'user', text, toolCalls: 0 }]);

        try {
            const r = await http.post<TurnDto>(`/AiDraft/${draft.id}/turn`, undefined, { text });
            if (!r.data.ok) {
                setError(r.data.error ?? 'Der Zug ist fehlgeschlagen.');
            }

            /*
             * Der Teilstand wandert sofort weiter — Angebotstexte und Sonderleistungen, nicht die
             * Positionen. Vorher musste der Vertriebler dafür „Übernehmen" drücken, und wer das
             * mitten im Telefonat nicht tat, hatte am Ende ein Angebot mit leerer
             * Projektbeschreibung.
             */
            if (r.data.partial) onPartial?.(r.data.partial);
            await reload(draft.id);
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
            setCurrentTool(null);
        }
    };

    /**
     * Spiegelt den aktuellen Stand in den Kalkulator — **ohne** das Gespräch zu beenden.
     *
     * Im Telefonat wird zwischendurch übernommen und danach weitergesprochen. Den Dialog hier zu
     * schließen hieße, nach jedem Zwischenstand von vorn anzufangen.
     */
    const apply = async () => {
        if (!draft) return;
        setBusy(true);
        try {
            const r = await http.post<{ project: ProjectInput; notes?: string | null }>(
                `/AiDraft/${draft.id}/apply`);
            onApply(r.data.project, r.data.notes);
            setAppliedAt(new Date());
            enqueueSnackbar('Stand in den Kalkulator übernommen — das Gespräch läuft weiter.',
                { variant: 'success' });
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    /** Rückmeldung an die Katalogpflege — kostet im Gespräch drei Sekunden. */
    const submitFeedback = async (data: { term: string; context: string; requirementId: string | null }) => {
        if (!draft || !feedbackKind) return;
        try {
            await http.post(`/AiDraft/${draft.id}/feedback`, undefined, {
                kind: feedbackKind,
                term: data.term,
                context: data.context || null,
                requirementId: data.requirementId,
            });
            enqueueSnackbar('Danke — landet in der Pflegeliste im Stammdaten-Editor.', { variant: 'success' });
        } catch (e) {
            context.apiError(e);
        }
    };

    /**
     * Nimmt abgelegte oder ausgewählte Dateien an.
     *
     * Nacheinander und nicht gebündelt: eine abgelehnte Datei — zu groß, falsches Format — soll die
     * anderen nicht mitreißen, und der Grund soll benennbar bleiben.
     */
    const upload = useCallback(async (files: FileList | File[]) => {
        if (!draft) return;
        setUploading(true);
        setError(null);

        for (const file of Array.from(files)) {
            const form = new FormData();
            form.append('file', file);
            try {
                await context.api.request({
                    path: `/AiDraft/${draft.id}/attachment`,
                    method: 'POST',
                    body: form,
                    secure: true,
                });
            } catch (e) {
                const detail = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
                setError(detail ?? `${file.name} konnte nicht hinzugefügt werden.`);
            }
        }

        setUploading(false);
        setAttachmentToken((t) => t + 1);
    }, [draft, context.api]);

    const openQuestions = useMemo(() => questions.filter((q) => q.state === 'Open'), [questions]);

    /** Kopfzeile — in beiden Zuständen dieselbe. */
    const header = (
        <Stack
            direction="row" spacing={1}
            sx={{
                alignItems: 'center', flexShrink: 0,
                px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider',
            }}
        >
            <Box sx={{ color: 'primary.main', display: 'flex' }}><FontAwesomeIcon icon={faRobot} /></Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Angebotsassistent</Typography>
            {protocol.length > 0 && (
                <Chip size="small" variant="outlined" label={`${protocol.length} protokolliert`} />
            )}

            {/*
              * Die offenen Punkte gehören in die Kopfzeile und nicht in einen Reiter: sie sind das,
              * was der Vertriebler nach dem Telefonat abarbeitet. Was man nicht sieht, klärt niemand.
              */}
            {openQuestions.length > 0 && (
                <Tooltip title="Offene Punkte anzeigen">
                    <Chip
                        size="small" color="warning" variant={showCaseFile ? 'filled' : 'outlined'}
                        label={`${openQuestions.length} offen`}
                        onClick={() => setShowCaseFile((v) => !v)}
                    />
                </Tooltip>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {draft && (
                <Tooltip title="Rückmeldung an die Katalogpflege">
                    <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </IconButton>
                </Tooltip>
            )}
            {onClose && (
                <Tooltip title="Schließen — das Gespräch bleibt erhalten">
                    <IconButton size="small" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );

    if (!draft) {
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {header}
                <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
                    <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ fontSize: '2.5rem', color: 'action.disabled' }}>
                            <FontAwesomeIcon icon={faRobot} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Souffliert während des Telefonats: schlägt die nächste Frage vor, ergänzt
                            Positionen und rechnet mit. Du entscheidest am Ende, was übernommen wird.
                        </Typography>
                        <Button variant="contained" onClick={start} disabled={starting}
                            startIcon={starting ? <CircularProgress size={16} /> : <FontAwesomeIcon icon={faRobot} />}>
                            Neues Gespräch
                        </Button>
                    </Stack>

                    {/*
                      * Weiterführen statt neu erzählen. Ein Gespräch ist mit dem Telefonat nicht zu
                      * Ende — der Kunde ruft am nächsten Tag zurück, und dann soll dort
                      * weitergemacht werden, wo aufgehört wurde.
                      */}
                    {recent.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="overline" color="text.secondary">
                                Fortsetzen
                            </Typography>

                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                {recent.map((d) => (
                                    <Box
                                        key={d.id}
                                        onClick={() => resume(d.id)}
                                        sx={{
                                            px: 1.5, py: 1, borderRadius: 1, cursor: 'pointer',
                                            border: 1,
                                            borderColor: projektId && d.projektId === projektId
                                                ? 'primary.main' : 'divider',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, flexGrow: 1, textAlign: 'left',
                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap' }}
                                            >
                                                {d.name}
                                            </Typography>
                                            {d.openQuestions > 0 && (
                                                <Chip
                                                    size="small" color="warning" variant="outlined"
                                                    label={`${d.openQuestions} offen`}
                                                />
                                            )}
                                        </Stack>
                                        <Typography
                                            variant="caption" color="text.secondary"
                                            sx={{ display: 'block', textAlign: 'left' }}
                                        >
                                            {new Date(d.updatedAt).toLocaleString()}
                                            {d.anlagenart ? ` · ${d.anlagenart}` : ''}
                                            {d.projektart && d.projektart !== 'Katalogkonfiguration'
                                                ? ` · ${d.projektart}` : ''}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        // Drei feste Zonen: Kopf, scrollender Verlauf, angeheftete Eingabe. Nur die mittlere
        // scrollt — vorher schwamm die Eingabe in der Mitte, weil zwei Ebenen gescrollt haben.
        <Box
            /*
             * Nur ANKÜNDIGEN, nicht annehmen: dieses Element merkt, dass eine Datei über dem Panel
             * schwebt, und legt daraufhin die Ablegefläche darüber. Der Empfang selbst passiert dort.
             *
             * Der erste Versuch hatte den Empfang hier — und er funktionierte nicht. Das Textfeld
             * und der scrollende Verlauf behandeln ein Ablegen selbst: ein Textfeld fügt den
             * Dateinamen als Text ein und verbraucht das Ereignis. Wer über die Mitte des Panels
             * loslässt, traf also nie den Empfänger.
             */
            onDragEnter={(e) => {
                if (e.dataTransfer.types.includes('Files')) setDragOver(true);
            }}
            onDragOver={(e) => {
                if (e.dataTransfer.types.includes('Files')) {
                    e.preventDefault();
                    setDragOver(true);
                }
            }}
            sx={{
                height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
                position: 'relative',
            }}
        >
            {header}

            {draft && <AttachmentBar draftId={draft.id} reloadToken={attachmentToken} />}

            {/*
              * Die Gesprächsakte: was der Assistent über das Geschäft weiß, außerhalb des Verlaufs.
              * Zugeklappt, weil im Telefonat der Dialog zählt — aufgeklappt genau dann, wenn nach
              * dem Auflegen die offenen Punkte abgearbeitet werden.
              */}
            {showCaseFile && (
                <Box sx={{
                    flexShrink: 0, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider',
                    bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
                    maxHeight: '40%', overflowY: 'auto',
                }}>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {draft.anlagenart && (
                            <Chip size="small" variant="outlined" label={draft.anlagenart} />
                        )}
                        {draft.projektart && (
                            <Chip size="small" variant="outlined" label={draft.projektart} />
                        )}
                        {draft.compacted && (
                            <Tooltip title="Ältere Züge stehen zusammengefasst im Kontext — das hält ein langes Gespräch bezahlbar.">
                                <Chip size="small" variant="outlined" label="Verlauf verdichtet" />
                            </Tooltip>
                        )}
                    </Stack>

                    {participants.length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="overline" color="text.secondary">Beteiligte</Typography>
                            {participants.map((p) => (
                                <Typography key={`${p.mail ?? ''}${p.name}`} variant="caption"
                                    sx={{ display: 'block' }}>
                                    <strong>{p.role}</strong>
                                    {' · '}
                                    {p.name}
                                    {p.organisation ? `, ${p.organisation}` : ''}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Typography variant="overline" color="text.secondary">Noch zu klären</Typography>
                    {openQuestions.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Nichts offen.
                        </Typography>
                    )}
                    {openQuestions.map((q) => (
                        <Box key={q.number} sx={{ mb: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{q.text}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {[q.recipient && `an ${q.recipient}`, q.language, q.detail]
                                    .filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {dragOver && (
                <Box
                    /*
                     * Diese Fläche liegt über allem und nimmt entgegen. pointerEvents bleibt aktiv —
                     * genau deshalb kann kein Kindelement das Ablegen mehr abfangen. Sie erscheint
                     * erst beim Ziehen und ist danach wieder weg, stört also nichts.
                     */
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        if (e.dataTransfer.files.length > 0) upload(e.dataTransfer.files);
                    }}
                    sx={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                        border: (t) => `2px dashed ${t.palette.primary.main}`,
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: 'primary.main', pointerEvents: 'none' }}>
                        Bild, PDF oder E-Mail hier ablegen
                    </Typography>
                </Box>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {/* Zurück zur Liste. Das offene Gespräch bleibt erhalten — nichts geht verloren. */}
                <MenuItem onClick={() => { setMenuAnchor(null); setDraft(null); }}>
                    <FontAwesomeIcon icon={faClockRotateLeft} style={{ marginRight: 8 }} />
                    Anderes Gespräch
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setFeedbackKind('MissingQuestion'); }}>
                    <FontAwesomeIcon icon={faCircleQuestion} style={{ marginRight: 8 }} />
                    Frage hat gefehlt
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); setFeedbackKind('UnknownTerm'); }}>
                    <FontAwesomeIcon icon={faThumbsDown} style={{ marginRight: 8 }} />
                    Begriff nicht erkannt
                </MenuItem>
            </Menu>


            <Box ref={scrollRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5 }}>
                {messages.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Schreib mit, was der Kunde sagt — auch stichwortartig. Beispiel:
                        „Feuerwehr, zwei Standorte, vier Motorola MTM 800".
                    </Typography>
                )}

                {/*
                  * Die Markenfarbe akzentuiert, sie flutet nicht: der Assistent bekommt einen
                  * farbigen Streifen und ruhigen Grund, der eigene Beitrag eine leicht getönte
                  * Blase rechts. Zwei Vollton-Blöcke übereinander waren eine Wand, und der Text —
                  * auf den es ankommt — ging darin unter.
                  */}
                {messages.map((m, i) => {
                    const own = m.role === 'user';

                    if (own) {
                        return (
                            <Box key={i} sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box
                                    sx={{
                                        maxWidth: '85%',
                                        px: 1.5, py: 1,
                                        borderRadius: 2,
                                        borderTopRightRadius: 4,
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {m.text}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            key={i}
                            sx={{
                                mb: 2,
                                pl: 1.5,
                                borderLeft: 3,
                                borderColor: 'primary.main',
                            }}
                        >
                            {/* Das Modell antwortet in Markdown — ohne Rendern stünde hier
                                "**Frage an den Kunden:**" wörtlich. */}
                            <MarkdownRenderer
                                sx={{
                                    color: 'text.primary',
                                    '& p': { m: 0, mb: 0.75, fontSize: '0.875rem', lineHeight: 1.55 },
                                    '& p:last-child': { mb: 0 },
                                    '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5, fontSize: '0.875rem' },
                                    '& li': { mb: 0.25 },
                                    // Die Rückfrage an den Kunden ist das, was abgelesen wird —
                                    // sie hebt sich über die Farbe ab, nicht über einen Kasten.
                                    '& strong': { fontWeight: 600, color: 'primary.main' },
                                }}
                            >
                                {m.text}
                            </MarkdownRenderer>

                            {m.toolCalls > 0 && (
                                <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ display: 'block', mt: 0.75 }}
                                >
                                    <FontAwesomeIcon icon={faScrewdriverWrench} style={{ marginRight: 4 }} />
                                    {m.toolCalls} Schritte
                                </Typography>
                            )}
                        </Box>
                    );
                })}

                {busy && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                            {currentTool ? `${currentTool} …` : 'denkt nach …'}
                        </Typography>
                    </Stack>
                )}
            </Box>

            <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', px: 2, py: 1.5 }}>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {dictation.error && <Alert severity="warning" sx={{ mb: 1 }}>{dictation.error}</Alert>}
                {busy && <LinearProgress sx={{ mb: 1 }} />}

                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                    <input
                        ref={fileInput}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,.eml,message/rfc822"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (e.target.files?.length) upload(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    <Tooltip title="Bild, PDF oder E-Mail hinzufügen">
                        <IconButton
                            size="small"
                            disabled={busy || uploading}
                            onClick={() => fileInput.current?.click()}
                        >
                            {uploading
                                ? <CircularProgress size={16} />
                                : <FontAwesomeIcon icon={faPaperclip} />}
                        </IconButton>
                    </Tooltip>
                    <TextField
                        fullWidth size="small" multiline maxRows={6}
                        placeholder="Was sagt der Kunde?"
                    value={input}
                    disabled={busy}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        // Enter schickt, Shift+Enter macht eine neue Zeile — im Telefonat zählt
                        // jeder gesparte Handgriff.
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void send();
                        }
                    }}
                />
                <Tooltip title={dictation.recording ? 'Aufnahme beenden' : 'Diktieren'}>
                    <span>
                        <IconButton
                            color={dictation.recording ? 'error' : 'default'}
                            disabled={busy || dictation.busy}
                            onClick={dictation.recording ? dictation.stop : dictation.start}
                        >
                            <FontAwesomeIcon icon={dictation.recording ? faStop : faMicrophone} />
                        </IconButton>
                    </span>
                    </Tooltip>
                    <IconButton color="primary" disabled={busy || !input.trim()} onClick={send}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </IconButton>
                </Stack>

                <Button variant="contained" size="small" fullWidth sx={{ mt: 1.5 }}
                    onClick={apply} disabled={busy || messages.length === 0}>
                    In den Kalkulator übernehmen
                </Button>

                <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', mt: 0.75 }}>
                    {appliedAt
                        ? `zuletzt übernommen ${appliedAt.toLocaleTimeString()}`
                        : draft.iterations > 0
                            ? `${draft.iterations} Schritte · ${Math.round(draft.durationMs / 1000)} s`
                            : 'Texte und Sonderleistungen fließen laufend mit'}
                </Typography>

                {/*
                  * Der Aufwand des Gesprächs, sichtbar statt in der Abrechnung. Der Anteil aus dem
                  * Zwischenspeicher ist die Zahl, auf die es ankommt: er kostet ein Zehntel, und
                  * fällt er ab, wird ein langes Gespräch teuer.
                  */}
                {draft.inputTokens > 0 && (
                    <Typography variant="caption" color="text.disabled"
                        sx={{ display: 'block', textAlign: 'center' }}>
                        {`${Math.round((draft.inputTokens + draft.outputTokens) / 1000)}k Token`}
                        {draft.cachedTokens > 0
                            && ` · ${Math.round((draft.cachedTokens / draft.inputTokens) * 100)}% aus dem Zwischenspeicher`}
                        {draft.compacted && ' · Verlauf verdichtet'}
                    </Typography>
                )}
            </Box>

            <AiFeedbackDialog
                open={feedbackKind !== null}
                kind={feedbackKind ?? 'UnknownTerm'}
                defaultContext={messages.slice(-2).map((m) => m.text).join(' | ')}
                onClose={() => setFeedbackKind(null)}
                onSubmit={submitFeedback}
            />
        </Box>
    );
};

export default AiAssistantPanel;
